import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Archive, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface LenderProfile {
  id: string;
  lender_name: string;
  short_name: string | null;
  loan_start_date: string;
  is_active: boolean;
  created_at: string;
}

interface LoanNote {
  id: string;
  total_funds_available: number;
  draws_allowed: number;
  last_term_advance_date_allowed: string | null;
  sofr_on_start: number;
  spread_on_start: number;
}

interface SOFRChange {
  new_sofr_percentage: number;
  apply_from_date: string;
}

interface SpreadChange {
  new_spread_percentage: number;
  apply_from_date: string;
}

interface LenderTransaction {
  amount: number;
  transaction_type: string;
}

interface EnhancedLenderCardProps {
  lender: LenderProfile;
  onEdit: () => void;
  onViewLoans: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onStatusChange: (status: string) => void;
}

export const EnhancedLenderCard = ({
  lender,
  onEdit,
  onViewLoans,
  onArchive,
  onDelete,
  onStatusChange,
}: EnhancedLenderCardProps) => {
  const [loanNote, setLoanNote] = useState<LoanNote | null>(null);
  const [currentSOFR, setCurrentSOFR] = useState<number>(0);
  const [currentSpread, setCurrentSpread] = useState<number>(0);
  const [drawnAmount, setDrawnAmount] = useState<number>(0);
  const [advanceCount, setAdvanceCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("Active");

  useEffect(() => {
    fetchLenderData();
  }, [lender.id]);

  const fetchLenderData = async () => {
    try {
      setLoading(true);

      // Fetch loan note
      const { data: noteData, error: noteError } = await supabase
        .from("loan_notes")
        .select("*")
        .eq("lender_profile_id", lender.id)
        .single();

      if (noteError && noteError.code !== "PGRST116") throw noteError;
      setLoanNote(noteData);

      if (noteData) {
        // Fetch latest SOFR change
        const { data: sofrData } = await supabase
          .from("sofr_changes")
          .select("*")
          .eq("loan_note_id", noteData.id)
          .order("apply_from_date", { ascending: false })
          .limit(1);

        if (sofrData && sofrData.length > 0) {
          setCurrentSOFR(sofrData[0].new_sofr_percentage);
        } else {
          setCurrentSOFR(noteData.sofr_on_start);
        }

        // Fetch latest spread change
        const { data: spreadData } = await supabase
          .from("spread_changes")
          .select("*")
          .eq("loan_note_id", noteData.id)
          .order("apply_from_date", { ascending: false })
          .limit(1);

        if (spreadData && spreadData.length > 0) {
          setCurrentSpread(spreadData[0].new_spread_percentage);
        } else {
          setCurrentSpread(noteData.spread_on_start);
        }
      }

      // Fetch transactions
      const { data: transactionData } = await supabase
        .from("lender_transactions")
        .select("*")
        .eq("lender_profile_id", lender.id);

      if (transactionData) {
        const drawn = transactionData
          .filter((t) => t.transaction_type === "draw" || t.transaction_type === "advance")
          .reduce((sum, t) => sum + Number(t.amount), 0);
        setDrawnAmount(drawn);

        const advances = transactionData.filter(
          (t) => t.transaction_type === "advance"
        ).length;
        setAdvanceCount(advances);
      }
    } catch (error) {
      console.error("Error fetching lender data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onArchive();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  if (loading) {
    return (
      <Card className="p-4">
        <div className="text-center text-muted-foreground">Loading...</div>
      </Card>
    );
  }

  const currentRate = currentSOFR + currentSpread;
  const capitalAvailable = loanNote
    ? loanNote.total_funds_available - drawnAmount
    : 0;
  const annualInterestCost = (drawnAmount * currentRate) / 100;
  const advancesAvailable = loanNote ? loanNote.draws_allowed - advanceCount : 0;

  return (
    <Card className="p-4 hover:shadow-lg transition-shadow">
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <h4 className="font-semibold text-lg">
            {lender.short_name || lender.lender_name}
          </h4>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleArchive}
              title="Archive"
            >
              <Archive className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              title="Delete"
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Status and Date */}
        <div className="flex items-center justify-between text-sm">
          <Select
            value={status}
            onValueChange={(value) => {
              setStatus(value);
              onStatusChange(value);
            }}
          >
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-muted-foreground">
            Since {new Date(lender.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>

        {/* Lends To */}
        <div className="text-sm text-muted-foreground">
          Lends to <span className="font-medium text-foreground">{lender.short_name || lender.lender_name}</span>
        </div>

        {/* Financial Details Grid */}
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Contracted Credit Line Value</span>
            <span className="font-medium">
              ${loanNote ? loanNote.total_funds_available.toLocaleString() : "0"}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-muted-foreground">Drawn so Far</span>
            <span className="font-medium">
              ${drawnAmount.toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-muted-foreground">Current Rate</span>
            <span className="font-medium">{currentRate.toFixed(2)}%</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Annual Interest Costs</span>
            <div className="flex items-center gap-2">
              <span className="font-medium">
                ${annualInterestCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
              <span className="text-xs text-muted-foreground">Compounds Annually</span>
            </div>
          </div>

          <div className="flex justify-between">
            <span className="text-muted-foreground">Accrued Interest</span>
            <span className="font-medium">$0</span>
          </div>

          <div className="flex justify-between">
            <span className="text-muted-foreground">Entities Financed</span>
            <span className="font-medium">1</span>
          </div>

          <div className="flex justify-between">
            <span className="text-primary">Loan Noted Issued</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewLoans();
              }}
              className="font-medium text-primary hover:underline"
            >
              {loanNote ? "1" : "0"}
            </button>
          </div>

          <div className="flex justify-between">
            <span className="text-muted-foreground">Capital Still Available</span>
            <span className="font-medium text-green-600">
              ${capitalAvailable.toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-muted-foreground">Term Advances Available</span>
            <span className="font-medium text-green-600">{advancesAvailable}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-muted-foreground">Term Advance Deadline</span>
            <span className="font-medium">
              {loanNote?.last_term_advance_date_allowed
                ? new Date(loanNote.last_term_advance_date_allowed).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                : "N/A"}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            Edit Profile
          </Button>
          <Button
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              onViewLoans();
            }}
          >
            Loans
          </Button>
        </div>
      </div>
    </Card>
  );
};
